'use client'

import { Music, Disc, Headphones, Radio, Heart, Sparkles, Zap, Star } from 'lucide-react'

const icons = [Music, Disc, Headphones, Radio, Heart, Sparkles, Zap, Star]

interface Particle {
  id: number
  Icon: typeof Music
  x: number
  y: number
  size: number
  delay: number
  duration: number
  opacity: number
}

function generateParticles(count: number): Particle[] {
  // A fixed seed keeps server and client markup identical during hydration.
  let seed = 42
  const random = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    return seed / 4294967296
  }

  return Array.from({ length: count }, (_, i) => ({
    id: i,
    Icon: icons[Math.floor(random() * icons.length)],
    x: random() * 100,
    y: random() * 100,
    size: 12 + random() * 16,
    delay: random() * 5,
    duration: 15 + random() * 10,
    opacity: 0.1 + random() * 0.2,
  }))
}

const particles = generateParticles(15)

export function AnimatedBackground() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 bg-black">
      {/* Neon Orbs */}
      <div className="orb orb-pink w-[500px] h-[500px] -top-[10%] -left-[10%] opacity-30" />
      <div className="orb orb-cyan w-[400px] h-[400px] top-[50%] -right-[5%] opacity-25" />
      <div className="orb orb-purple w-[600px] h-[600px] -bottom-[20%] left-[30%] opacity-20" />
      
      {/* Grid Lines */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
        }}
      />
      
      {/* Floating Particles */}
      {particles.map(({ id, Icon, x, y, size, delay, duration, opacity }) => (
        <div
          key={id}
          className="particle"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            opacity,
            animationDelay: `${delay}s`,
            animationDuration: `${duration}s`,
          }}
        >
          <Icon 
            style={{ width: size, height: size }} 
            className="text-white/30"
          />
        </div>
      ))}
      
      {/* Decorative Vinyl in Corner */}
      <div className="absolute -bottom-32 -right-32 w-96 h-96 vinyl animate-spin-slow opacity-20">
        <div className="vinyl-shine" />
      </div>
    </div>
  )
}
